import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";

import { useHistory, useParams, useLocation } from "react-router-dom";

import {
  Card,
  Box,
  Text,
  InlineStack,
  BlockStack,
  InlineGrid,
  Divider,
  Button,
  Page,
  Layout,
  Modal,
  Toast,
  Banner,
  IndexFilters,
  ChoiceList,
  Pagination,
  Badge,
  useSetIndexFiltersMode,
} from "@shopify/polaris";

import { ExternalIcon } from "@shopify/polaris-icons";

import {
  useGetAdminAccountQuery,
  useGetLendersQuery,
  useCreateAdminAccountReviewMutation,
  useCreateGoogleDriveFolderMutation,
} from "../../../services/api";

import AccountForm from "../../../components/Account/Form";
import FadeIn from "../../../components/FadeIn";
import AdminClientContactList from "../../../components/Admin/Client/ContactList";
import LoadingScreen from "../../../components/Auth/Loading";
import CreditSubmissionTable from "../../../components/Admin/CreditSubmission/CreditSubmissionTable";

import { CurrentContactContext } from "../../../contexts/Contact";

import { isMobile } from "react-device-detect";

import { AccountStatistics } from "./components/account-statistics";
import { AccountInformation } from "./components/account-information";
import { AccountOpportunities } from "./components/account-opportunities";

const AdminClientShow = (props) => {
  const history = useHistory();
  const location = useLocation();

  const [Ids, setIds] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLender, setSelectedLender] = useState([]);
  const [lenderChoiceList, setLenderChoiceList] = useState([]);
  const [queryValue, setQueryValue] = useState("");
  const [sortSelected, setSortSelected] = useState(["created_date asc"]);
  const [selected, setSelected] = useState(0);
  const limit = 25;

  const { mode, setMode } = useSetIndexFiltersMode();

  const { id: clientId } = useParams();
  const { currentContact, isOrganizationAdmin, isFinanceSpecialist } =
    useContext(CurrentContactContext);

  useEffect(() => {
    if (!clientId) {
      navigateToAdminAccountsIndex();
    }
  }, [clientId, navigateToAdminAccountsIndex]);

  const { client: clientObject } = location?.state?.client
    ? location.state
    : {
        client: { attributes: {} },
      };

  const { data: client = clientObject } = useGetAdminAccountQuery(clientId);
  const [createAccountReview, { isLoading: isCreatingAccountReview }] =
    useCreateAdminAccountReviewMutation();

  const [createGoogleDriveFolder, { isLoading: isCreatingGoogleDriveFolder }] =
    useCreateGoogleDriveFolderMutation();

  const { data: lenders = [] } = useGetLendersQuery();

  const onCreateNewView = async (value) => {
    await sleep(500);
    setItemStrings([...itemStrings, value]);
    setSelected(itemStrings.length);
    return true;
  };

  const navigateToAdminAccountsIndex = useCallback(() => {
    history.push("/admin");
  }, [history]);

  useEffect(() => {
    if (lenders.length > 0) {
      setLenderChoiceList(
        lenders.map((lender) => ({
          label: lender.attributes.name,
          value: lender.id,
        }))
      );
    }
  }, [lenders]);

  const handleFiltersQueryChange = useCallback((value) => {
    setQueryValue(value);
    setPage(1);
  }, []);

  const handleOnSelect = (viewIndex) => {
    setSelected(viewIndex);
    setQueryValue("");
    setPage(1);
  };

  const handleChangePage = (mode) => {
    setIsLoading(true);
    setPage((state) => {
      if (mode == "next" && state == Math.ceil(count / limit)) {
        return Math.ceil(count / limit);
      } else if (mode == "previous" && state == 1) {
        return 1;
      }

      if (mode == "next") {
        return state + 1;
      } else if (mode == "previous") {
        return state - 1;
      }
    });
  };

  const handleSelectLender = (lender) => {
    setSelectedLender(lender);
  };

  const filters = [
    {
      key: "lender",
      label: "Lender",
      filter: (
        <ChoiceList
          title="Lender"
          titleHidden
          choices={lenderChoiceList}
          selected={selectedLender || []}
          onChange={handleSelectLender}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const tablePagination = (
    <Pagination
      hasPrevious={page != 1}
      hasNext={page < Math.ceil(count / limit)}
      onPrevious={() => {
        handleChangePage("previous");
      }}
      onNext={() => {
        handleChangePage("next");
      }}
      label={
        <Text>
          Page {page} of {Math.ceil(count / limit)}
        </Text>
      }
    />
  );

  const [showEditAccountDetailsModal, setShowAccountDetailsModal] =
    useState(false);

  const handleEditAccountDetails = () => {
    setShowAccountDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowAccountDetailsModal(false);
    setShowReviewAccountModal(false);
  };

  const [clientFormDirty, setAccountFormDirty] = useState(false);

  const handleAccountEdit = (isDirty) => {
    setAccountFormDirty(isDirty);
  };
  const handleAccountUpdate = (data) => {
    handleCloseModal();
    setShowToast(true);
    setToastMessage("Account updated.");
  };

  const clientFormRef = useRef();

  const editAccountModal = (
    <Modal
      open={showEditAccountDetailsModal}
      onClose={handleCloseModal}
      title="Edit client"
      primaryAction={{
        content: "Save",
        onAction: () => clientFormRef.current.handleSubmitForm(),
        disabled: !clientFormDirty,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: handleCloseModal,
        },
      ]}
    >
      <AccountForm
        ref={clientFormRef}
        account={client}
        onCreateOrUpdate={handleAccountUpdate}
        onEdit={handleAccountEdit}
        isAdminContext={true}
      />
    </Modal>
  );

  const [showReviewAccountModal, setShowReviewAccountModal] = useState(false);

  const handleReviewAccount = () => {
    return createAccountReview({
      accountId: clientId,
    })
      .unwrap()
      .then(({ data }) => {
        handleCloseModal();
        setShowToast(true);
        setToastMessage("Account reviewed.");
        return { status: "success" };
      })
      .catch((error) => {
        alert("Failed to add review.");
        console.log(error);
      });
  };

  const openGoogleDriveFolderAction = () => {
    const requestBody = {
      template: "client"
    };

    return createGoogleDriveFolder({ clientId: clientId, ...requestBody })
      .then((response) => {
        if (response.data?.folder_url) {
          window.open(response.data.folder_url, "_blank");
          
          let message = "Google Drive folder opened";
          if (response.data.folder_recreated) {
            message = "Previous folder was deleted, created new folder";
          } else if (response.data.template_applied) {
            message += " with client template";
          }
          
          setShowToast(true);
          setToastMessage(message);
        }
        return { status: "success" };
      })
      .catch((error) => {
        console.log(error);
        setShowToast(true);
        setToastMessage("Failed to open Google Drive folder");
        return { status: "failure" };
      });
  };

  const reviewAccountModal = (
    <Modal
      open={showReviewAccountModal}
      onClose={handleCloseModal}
      title="Review client"
      primaryAction={{
        content: "Add review",
        onAction: handleReviewAccount,
        loading: isCreatingAccountReview,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: handleCloseModal,
        },
      ]}
      sectioned
    >
      Please check the legal business name and all other client details before
      adding your review.
    </Modal>
  );

  const [showToast, setShowToast] = useState(location?.state?.showToast);
  const [toastMessage, setToastMessage] = useState(
    location?.state?.toastMessage
  );
  const toggleShowToast = useCallback(
    () => setShowToast((active) => !active),
    []
  );
  const handleDismissToast = () => {
    const { state: currentState } = location;
    if (currentState?.toastMessage) {
      delete currentState.toastMessage;
    }
    if (currentState?.showToast) {
      delete currentState.showToast;
    }
    window.history.replaceState({ ...currentState }, "");
    toggleShowToast();
    setToastMessage();
  };
  const toastMarkup = showToast ? (
    <Toast content={toastMessage} onDismiss={handleDismissToast} />
  ) : undefined;

  const pageMarkup = (
    <FadeIn fadeIn>
      <Page
        title={client.attributes.name}
        subtitle={"Client Account"}
        secondaryActions={[
          {
            content: "Google Drive",
            onAction: openGoogleDriveFolderAction,
            loading: isCreatingGoogleDriveFolder,
            icon: ExternalIcon,
          },
          {
            content: "Review",
            onAction: () => setShowReviewAccountModal(true),
          },
        ]}
        fullWidth
      >
        <Layout>
          {!client.attributes.reviewed && (
            <Layout.Section>
              <Banner
                title="This client has not been reviewed by a Fortify admin."
                action={{
                  content: "Review",
                  onAction: () => setShowReviewAccountModal(true),
                }}
                tone="warning"
              ></Banner>
            </Layout.Section>
          )}

          <Layout.Section primary>
            <AccountStatistics client={client} />
          </Layout.Section>

          <Layout.Section primary>
            <AccountInformation
              client={client}
              handleEditAccountDetails={handleEditAccountDetails}
            />
            <br />
            <AccountOpportunities client={client} />
            <br />
            <Card padding="0">
              <Box padding="400" paddingBlockEnd={"0"}>
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h2">
                    Credit submissions
                  </Text>
                </InlineStack>
              </Box>
              <IndexFilters
                sortSelected={sortSelected}
                onSort={setSortSelected}
                queryValue={queryValue}
                queryPlaceholder={""}
                onQueryChange={handleFiltersQueryChange}
                onQueryClear={() => setQueryValue("")}
                cancelAction={{
                  onAction: () => {},
                  disabled: false,
                  loading: false,
                }}
                tabs={[]}
                selected={selected}
                onSelect={handleOnSelect}
                canCreateNewView={true}
                onCreateNewView={onCreateNewView}
                filters={filters}
                appliedFilters={[]}
                onClearAll={() => {}}
                mode={mode}
                setMode={setMode}
                filteringAccessibilityTooltip="Search (F)"
              />
              <CreditSubmissionTable
                setCount={setCount}
                page={page}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                limit={limit}
                setIds={setIds}
                queryValue={queryValue}
                clientIdFilter={clientId}
                clientShowPage={true}
                lender={selectedLender.join(",")}
              />
              <InlineStack align="center">
                <div style={{ padding: "1.3rem" }}>
                  {count > 0 ? tablePagination : <></>}
                </div>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>

        {editAccountModal}
        {reviewAccountModal}
        {toastMarkup}
      </Page>
    </FadeIn>
  );

  return currentContact ? pageMarkup : <LoadingScreen />;
};

export default AdminClientShow;
